import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";

import { encuestaSatisfaccionService } from "../../services/encuestaSatisfaccionService";

/**
 * Encuesta de satisfacción de GoEcoSystem para el personal, bloqueante, al
 * entrar a /staff.
 *
 * El backend decide si toca y confirma que se respondió; aquí solo se pinta
 * el iframe y se escuchan sus mensajes. Un fallo antes de mostrarla deja la
 * pantalla igual que siempre; si falla con la encuesta abierta se ofrece
 * reintentar o cerrar, y nadie queda encerrado.
 *
 * `clinicId` y `nit` salen de la clínica del roster (`services/clinicsM.js`).
 * Sin NIT, el backend pide la encuesta sin él y la API muestra su selector de
 * clínica dentro del iframe. Con un NIT que la API no reconoce, el backend
 * responde `SIN_NIT_CLINICA` y aquí no se pinta nada.
 */

const FUENTE = "encuestas-satisfaccion";
// Vigilante del iframe: si en este tiempo no llega ningún mensaje válido se
// pide un token nuevo, una sola vez por montaje o por clic en Reintentar, y
// si vuelve a pasar se muestra el error. El formulario avisa con
// `encuesta:resize` apenas pinta.
const ESPERA_IFRAME_MS = 10000;
// El iframe no avisa cuando un guardado falla, por ejemplo con el token de
// render vencido (30 min por defecto en la API). Pasado este tiempo sin
// terminar se ofrecen Reintentar y Cerrar, sin quitar la encuesta.
const ESPERA_RESPUESTA_MS = 20 * 60 * 1000;
const ALTO_INICIAL_PX = 480;
const ALTO_MAXIMO_PX = 4000;

const useStyles = makeStyles((theme) => ({
  titulo: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  contenido: {
    padding: 0,
  },
  iframe: {
    display: "block",
    width: "100%",
    border: 0,
  },
  aviso: {
    margin: theme.spacing(2, 2, 0),
  },
}));

function origenDe(url) {
  try {
    const { protocol, origin } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? origin : "";
  } catch {
    return "";
  }
}

export default function EncuestaSatisfaccion({ clinicId, nit }) {
  const classes = useStyles();
  const [encuesta, setEncuesta] = useState(null); // { url, origen }
  const [fase, setFase] = useState("cargando"); // cargando | lista | confirmando | error
  const [error, setError] = useState("");
  const [alto, setAlto] = useState(null);
  const [demorada, setDemorada] = useState(false); // lleva mucho abierta sin terminar
  const iframeRef = useRef(null);
  const ocupadoRef = useRef(false); // una sola petición en vuelo
  const reintentoRef = useRef(false); // el reintento automático ya se usó
  const senalesRef = useRef(0); // mensajes válidos recibidos del iframe

  const abrir = useCallback((url) => {
    const origen = origenDe(url);
    if (!origen) return false;
    setEncuesta({ url, origen });
    setFase("cargando");
    setError("");
    setAlto(null);
    setDemorada(false);
    return true;
  }, []);

  const fallar = useCallback((mensaje) => {
    setError(mensaje);
    setFase("error");
  }, []);

  const cerrar = useCallback(() => setEncuesta(null), []);

  const aplicar = useCallback(
    (datos) => {
      if (datos?.elegible) {
        if (!abrir(datos.url)) fallar("No fue posible cargar la encuesta.");
      } else if (datos && !datos.degradado) {
        // Ya no aplica: por ejemplo, la respondió en otra pestaña.
        cerrar();
      } else {
        fallar("El servicio de encuestas no está disponible en este momento.");
      }
    },
    [abrir, cerrar, fallar]
  );

  // Reintentar a mano: trae de nuevo su propio reintento automático.
  const recargar = useCallback(async () => {
    if (ocupadoRef.current) return;
    ocupadoRef.current = true;
    reintentoRef.current = false;
    setFase("cargando");
    setError("");
    let datos = null;
    try {
      datos = await encuestaSatisfaccionService.preparar(clinicId, nit);
    } catch {
      datos = null;
    } finally {
      ocupadoRef.current = false;
    }
    aplicar(datos);
  }, [clinicId, nit, aplicar]);

  // Reintento del vigilante. Si mientras llega el token el iframe actual da
  // señales de vida, el token se descarta: no se reemplaza una encuesta que ya
  // cargó ni se pisa una confirmación en curso.
  const reintentarSolo = useCallback(async () => {
    const senales = senalesRef.current;
    let datos = null;
    try {
      datos = await encuestaSatisfaccionService.preparar(clinicId, nit);
    } catch {
      datos = null;
    }
    if (senalesRef.current !== senales || ocupadoRef.current) return;
    aplicar(datos);
  }, [clinicId, nit, aplicar]);

  // Solo el backend libera la encuesta: el mensaje del iframe no basta.
  const confirmar = useCallback(async () => {
    if (ocupadoRef.current) return;
    ocupadoRef.current = true;
    setFase("confirmando");
    try {
      const datos = await encuestaSatisfaccionService.confirmar(clinicId, nit);
      if (datos?.completada === true) {
        cerrar();
      } else {
        fallar(
          datos?.degradado
            ? "No pudimos confirmar tu respuesta en este momento."
            : "Todavía no vemos tu respuesta registrada."
        );
      }
    } catch {
      fallar("No pudimos confirmar tu respuesta en este momento.");
    } finally {
      ocupadoRef.current = false;
    }
  }, [clinicId, nit, cerrar, fallar]);

  // Al entrar, el backend decide si toca. Si algo falla, la pantalla sigue igual.
  useEffect(() => {
    let vigente = true;
    encuestaSatisfaccionService
      .preparar(clinicId, nit)
      .then((datos) => {
        if (vigente && datos?.elegible) abrir(datos.url);
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [clinicId, nit, abrir]);

  useEffect(() => {
    if (!encuesta || fase !== "cargando") return undefined;
    const vigilante = setTimeout(() => {
      // Un Reintentar a mano en vuelo ya está pidiendo el token: al abrir la
      // encuesta nueva el vigilante vuelve a empezar.
      if (ocupadoRef.current) return;
      if (reintentoRef.current) {
        fallar("No fue posible cargar la encuesta.");
        return;
      }
      reintentoRef.current = true;
      reintentarSolo();
    }, ESPERA_IFRAME_MS);
    return () => clearTimeout(vigilante);
  }, [encuesta, fase, fallar, reintentarSolo]);

  useEffect(() => {
    if (!encuesta || fase !== "lista") return undefined;
    const reloj = setTimeout(() => setDemorada(true), ESPERA_RESPUESTA_MS);
    return () => clearTimeout(reloj);
  }, [encuesta, fase]);

  useEffect(() => {
    if (!encuesta) return undefined;

    const alRecibir = (event) => {
      const datos = event.data;
      if (
        event.origin !== encuesta.origen ||
        event.source !== iframeRef.current?.contentWindow ||
        !datos ||
        typeof datos !== "object" ||
        datos.source !== FUENTE ||
        typeof datos.type !== "string"
      ) {
        return;
      }

      senalesRef.current += 1;

      switch (datos.type) {
        case "encuesta:completed":
        case "encuesta:not-eligible":
          confirmar();
          break;
        case "encuesta:error":
          fallar("La encuesta no pudo guardarse. Intenta de nuevo.");
          break;
        case "encuesta:clinica-seleccionada":
          // El iframe navega a la encuesta de la clínica elegida: el
          // vigilante vuelve a cubrir esa carga.
          setDemorada(false);
          setFase((actual) => (actual === "confirmando" ? actual : "cargando"));
          break;
        case "encuesta:resize": {
          const altura = Number(datos.height);
          if (Number.isFinite(altura) && altura > 0) {
            setAlto(Math.min(Math.ceil(altura), ALTO_MAXIMO_PX));
          }
          setFase((actual) => (actual === "cargando" ? "lista" : actual));
          break;
        }
        default:
          setFase((actual) => (actual === "cargando" ? "lista" : actual));
      }
    };

    window.addEventListener("message", alRecibir);
    return () => window.removeEventListener("message", alRecibir);
  }, [encuesta, confirmar, fallar]);

  if (!encuesta) return null;

  const conSalida = fase === "error" || (fase === "lista" && demorada);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      // Mientras esté pendiente no se cierra con el fondo ni con Escape.
      onClose={() => {}}
      aria-labelledby="encuesta-satisfaccion-titulo"
    >
      <DialogTitle id="encuesta-satisfaccion-titulo" className={classes.titulo}>
        Encuesta de satisfacción
      </DialogTitle>

      {(fase === "cargando" || fase === "confirmando") && (
        <LinearProgress aria-label={fase === "confirmando" ? "Confirmando tu respuesta" : "Cargando la encuesta"} />
      )}

      {fase === "error" && (
        <Alert severity="error" className={classes.aviso}>
          {error} Puedes reintentar o cerrarla y seguir trabajando; te la mostraremos en tu próximo
          ingreso.
        </Alert>
      )}

      {fase === "lista" && demorada && (
        <Alert severity="info" className={classes.aviso}>
          ¿La encuesta dejó de responder? Puedes reintentar o cerrarla y seguir trabajando; te la
          mostraremos en tu próximo ingreso.
        </Alert>
      )}

      <DialogContent className={classes.contenido}>
        <iframe
          key={encuesta.url}
          ref={iframeRef}
          src={encuesta.url}
          title="Encuesta de satisfacción"
          sandbox="allow-scripts allow-same-origin allow-forms"
          referrerPolicy="no-referrer"
          className={classes.iframe}
          style={{ height: `${alto || ALTO_INICIAL_PX}px` }}
        />
      </DialogContent>

      {conSalida && (
        <DialogActions>
          <Button onClick={cerrar}>Cerrar</Button>
          <Button onClick={recargar} color="primary" variant="contained">
            Reintentar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
